import { isNotNullOrUndefined } from '../common/lang/object';
import { BuilderDocumentFormat } from './builderDocumentFormat';
import { Layer } from './layers/layer';
import { Section } from './sections/section';
import { PrintUnits, convertUnits } from './print/printUnits';
import { BuilderPrintDocument } from './print/builderPrintDocument';
import { BuilderAnimation } from './animation';
import { BuilderVideoDocument } from './video/builderVideoDocument';
import { BuilderMultiImageOption } from './multiImage';

export interface BuilderDocumentDimensions {
    width: number;
    height: number;
    unit: string;
}

export interface EditableField {
    layerId?: number;
    label: string;
    path: string;
    control: string;
}

export interface SimpleEditorField {
    layer: BuilderDocument | Layer | Section;
    value: unknown;
    config: EditableField;
}

export interface TextSubstitutionField {
    displayName: string;
    key: string;
}

export type TextSubstitutionValues = { [key: string]: string | undefined };

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
export interface ChannelDataBase {}

export interface FacebookChannelData extends ChannelDataBase {
    caption: string | null;
}

export interface InstagramChannelData extends ChannelDataBase {
    caption: string | null;
}

export interface LinkedInChannelData extends ChannelDataBase {
    caption: string | null;
}

export interface TwitterChannelData extends ChannelDataBase {
    status: string | null;
}

export interface TermsAndConditionsChannelData extends ChannelDataBase {
    text: string | null;
    url: string | null;
}

export type ChannelData =
    | FacebookChannelData
    | InstagramChannelData
    | LinkedInChannelData
    | TermsAndConditionsChannelData
    | TwitterChannelData;

export interface ChannelDataValues {
    facebook?: FacebookChannelData;
    instagram?: InstagramChannelData;
    linkedIn?: LinkedInChannelData;
    twitter?: TwitterChannelData;
    termsAndConditions?: TermsAndConditionsChannelData;
}

export type ChannelName = keyof ChannelDataValues;

export class BuilderDocument {
    public title = 'New Document';

    public format: BuilderDocumentFormat = BuilderDocumentFormat.content;

    public formatVersion = '1' as const;

    public dimensions: BuilderDocumentDimensions = {
        height: 1080,
        unit: 'px',
        width: 1080,
    };

    public layers: Layer[] = [];

    public background = 'rgb(255, 255, 255)';

    public editableFields: EditableField[] = [];

    public textSubstitutionFields: TextSubstitutionField[] = [
        {
            displayName: 'Location',
            key: 'location',
        },
    ];

    public animation: BuilderAnimation | null = null;

    /*
     * TODO: As part of DS-9114 the multiImage field has been extracted into BuilderTemplate.
     * This field can (should?) be removed
     */
    public multiImage: BuilderMultiImageOption[] | null = null;

    public channelData?: ChannelDataValues = {};

    // TODO: Move bleed onto BuilderPrintDocument
    public bleed = {
        amount: null as number | null,
        units: null as PrintUnits | null,
    };

    public instructions: string | null = null;

    // This was the flag for the initial text rendering fix (DS-2647)
    public useOldTextRenderingLineHeightFix = false;

    // This is the new flag for the text rendering fix (DS-2925)
    public useTextRenderingLineHeightFix = true;

    public clipTextLayers = false;
}

export const defaultBleed = {
    amount: 5,
    units: PrintUnits.millimetres,
};

export interface IBuilderEmailDocument {
    title: string;
    subject?: string;
    format: BuilderDocumentFormat.email;
    formatVersion: '1';

    sections: Section[];
    templateHtml: string | null;

    editableFields: EditableField[];
    textSubstitutionFields: Array<{
        displayName: string;
        key: string;
    }>;
    channelData: ChannelDataValues;

    /**
     * This property was never used.
     *
     * @deprecated
     */
    images?: never[];

    instructions: string | null;

    useKeepTextFormattingHackV1?: boolean | undefined;
}

export class BuilderEmailDocument implements IBuilderEmailDocument {
    public title = 'New E-mail';

    public subject?: string;

    public format = BuilderDocumentFormat.email as const;

    public formatVersion = '1' as const;

    public sections: Section[] = [];

    public templateHtml: string | null = null;

    public editableFields: EditableField[] = [];

    public textSubstitutionFields = [
        {
            displayName: 'Location',
            key: 'location',
        },
    ];

    public channelData: ChannelDataValues = {};

    /**
     * This property was never used.
     *
     * @deprecated
     */
    public images?: never[] = [] as never[];

    public instructions: string | null = null;

    public useKeepTextFormattingHackV1?: boolean | undefined;
}

export function isContentBuilderDocument(
    document: BuilderDocument | BuilderEmailDocument | BuilderPrintDocument | BuilderVideoDocument,
): document is BuilderDocument {

    return !!document && document.format === BuilderDocumentFormat.content;
}

export function isPrintDocument(
    document: BuilderDocument | BuilderEmailDocument | BuilderPrintDocument | BuilderVideoDocument,
): document is BuilderPrintDocument {

    return !!document && document.format === BuilderDocumentFormat.print;
}

export function isVideoDocument(
    document: BuilderDocument | BuilderEmailDocument | BuilderPrintDocument | BuilderVideoDocument,
): document is BuilderVideoDocument {

    return !!document && document.format === BuilderDocumentFormat.video;
}

export function isEmailDocument(
    document: BuilderDocument | BuilderEmailDocument | BuilderPrintDocument | BuilderVideoDocument,
): document is BuilderEmailDocument {

    return !!document && document.format === BuilderDocumentFormat.email;
}

export function isMultiImageContentDocument(
    document: BuilderDocument | BuilderEmailDocument | BuilderPrintDocument | BuilderVideoDocument,
): document is BuilderDocument & { multiImage: NonNullable<BuilderDocument['multiImage']> } {

    return !!document && isContentBuilderDocument(document) && isNotNullOrUndefined(document.multiImage);
}

export function getBleedAmount(document: BuilderDocument, units = PrintUnits.pixels): number {
    if (document.format !== BuilderDocumentFormat.print) {
        return 0;
    }

    return (

        (document &&

            document.bleed &&
            document.bleed.amount &&
            document.bleed.units &&
            convertUnits(document.bleed.amount, document.bleed.units, units)) ||
        0
    );
}

export function isBuilderDocument(
    document: any,
): document is BuilderDocument | BuilderEmailDocument | BuilderPrintDocument | BuilderVideoDocument {
    return (
        isContentBuilderDocument(document) ||
        isPrintDocument(document) ||
        isVideoDocument(document) ||
        isEmailDocument(document)
    );
}

export function deDuplicateEditableFields(editableFields: EditableField[]): EditableField[] {
    const uniqueFields: EditableField[] = [];
    editableFields.forEach(editableField => {
        const hasBeenAdded = uniqueFields.some(
            field => field.layerId === editableField.layerId && field.path === editableField.path,
        );
        if (!hasBeenAdded) {
            uniqueFields.push(editableField);
        }
    });
    return uniqueFields;
}
